import type {RowData} from "@tanstack/react-table";
import type {FilterRule} from "crqlar-react-common/dist/models/audience/filter.model";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import {closeSnackbar, enqueueSnackbar} from "notistack";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {SERVER_TABLE_EXPANSION_COLUMN_ID} from "../ServerTableExpansionColumn";
import {useServerTableContext} from "../ServerTableContext";
import {SERVER_TABLE_SELECTION_COLUMN_ID} from "../ServerTableSelectionColumn";
import type {ServerTableInstance} from "../types";
import {
    downloadServerTableExport,
    ServerTableExportError,
    type ServerTableExportColumn,
    type ServerTableExportFormat,
    type ServerTableExportQuery,
    type ServerTableExportRun,
} from "./serverTableExport";

export interface ServerTableExportOptions<TRequest> {
    /** The list's key in the API's export registry: `POST /v3/aggregation/lists/{list}/export`, e.g. `daily-crm`. */
    list: string
    /** Names the file and its sheet; usually the print title. */
    title: string
    /**
     * The list's own request for the table's live query, as its `queryFn` builds it but without paging (pass
     * `serverTableExportLimit(sorting)` as its `limit`). Undefined when the list shows nothing by design, such as
     * every resource deselected.
     */
    buildRequest: (query: ServerTableExportQuery) => TRequest | undefined
    /** Prevents exporting. */
    disabled?: boolean
}

export interface ServerTableExportControl {
    /** Loading, failed, empty, or a read-only user: nothing to offer. */
    disabled: boolean
    /** The format being exported right now. */
    running?: ServerTableExportFormat
    /**
     * Every data column of the table with its header text, hidden ones included: the visible ones in display order,
     * then the hidden ones. A list's API export skips ids it does not know.
     */
    columns: ServerTableExportColumn[]
    /** The visible data columns' ids in display order: what an export carries unless told otherwise. */
    visibleColumnIds: string[]
    /** The list rows on the loaded page (a group is one row). */
    pageRowCount: number
    /** The list rows before the loaded page: page index × page size. */
    pageOffset: number
    /** The list rows of the whole filtered list, as the table's total says. */
    totalRowCount: number
    /**
     * Exports and saves the file: `maxRows` list rows from `offset` (every row from the top when missing) with
     * `columns` (the visible ones when missing). Snackbars say it started and that the file is saved; false when it
     * failed, after an error snackbar with the server's reason.
     */
    run: (format: ServerTableExportFormat, options?: ServerTableExportRun) => Promise<boolean>
}

const UTILITY_COLUMN_IDS = new Set([SERVER_TABLE_EXPANSION_COLUMN_ID, SERVER_TABLE_SELECTION_COLUMN_ID]);

const exportColumn = <TData extends RowData>(
    column: ReturnType<ServerTableInstance<TData>["getAllLeafColumns"]>[number],
): ServerTableExportColumn => {
    const header = column.columnDef.header;
    return {
        id: column.id,
        label: typeof header === "string" ? header : column.columnDef.meta?.headerLabel ?? column.id,
    };
};

/** The data columns as the header shows them: left-pinned, center, right-pinned, each with its header text. */
const visibleColumns = <TData extends RowData>(table: ServerTableInstance<TData>): ServerTableExportColumn[] => [
    ...table.getLeftVisibleLeafColumns(),
    ...table.getCenterVisibleLeafColumns(),
    ...table.getRightVisibleLeafColumns(),
]
    .filter(column => !UTILITY_COLUMN_IDS.has(column.id))
    .map(exportColumn);

/** The visible data columns in display order, then the hidden ones in definition order. */
const allColumns = <TData extends RowData>(
    table: ServerTableInstance<TData>,
    visible: ServerTableExportColumn[],
): ServerTableExportColumn[] => {
    const visibleIds = new Set(visible.map(column => column.id));
    const hidden = table.getAllLeafColumns()
        .filter(column => !UTILITY_COLUMN_IDS.has(column.id) && !visibleIds.has(column.id))
        .map(exportColumn);

    return [...visible, ...hidden];
};

/** What the table's own requests carry: trimmed search, its filter rules, the sort under its backend field. */
const liveQuery = <TData extends RowData>(
    table: ServerTableInstance<TData>,
    searchQuery: string,
    filterRules: FilterRule[],
): ServerTableExportQuery => {
    const sort = table.getState().sorting[0];
    const column = sort ? table.getAllLeafColumns().find(leaf => leaf.id === sort.id) : undefined;

    return {
        searchQuery: searchQuery.trim(),
        filterRules,
        sorting: sort ? {
            field: column?.columnDef.meta?.serverSortField ?? sort.id,
            direction: sort.desc ? "DESC" : "ASC",
        } : undefined,
    };
};

/**
 * The server-side CSV/Excel export of the table in the nearest `ServerTableProvider`: the rows of the live search,
 * filters and sort as the API renders them (one row per guest of a group), all of them or the first few, with the
 * picked columns and their header text. `ServerTableRailExport` is its rail action and `ServerTableExport` its
 * toolbar control; both open `ServerTableExportDialog`. The running state lives here, in the face, so it outlives the
 * dialog, which closes as soon as an export starts.
 */
export function useServerTableExport<TRequest>({
    list,
    title,
    buildRequest,
    disabled,
}: ServerTableExportOptions<TRequest>): ServerTableExportControl {
    const {t, i18n} = useTranslation();
    const {table, query, searchQuery, filterRules} = useServerTableContext();
    const readOnly = useReadOnly();
    const [running, setRunning] = useState<ServerTableExportFormat>();

    const request = buildRequest(liveQuery(table, searchQuery, filterRules));
    const totalRowCount = query.data?.totalElements ?? 0;
    const {pageIndex, pageSize} = table.getState().pagination;
    const nothingToExport = request === undefined || !totalRowCount;
    const visible = visibleColumns(table);
    const language = i18n.resolvedLanguage || i18n.language;

    const run = async (format: ServerTableExportFormat, {maxRows, offset, columns}: ServerTableExportRun = {}) => {
        if (running || request === undefined) return false;

        setRunning(format);
        // Stays until the file is saved or the export failed, then gives way to the outcome.
        const progress = enqueueSnackbar(t("Exporting {{count, number}} rows as {{format}}…", {
            count: maxRows ?? totalRowCount,
            format: format === "xlsx" ? t("Excel") : t("CSV"),
        }), {
            variant: "info",
            persist: true,
        });
        try {
            const fileName = await downloadServerTableExport(list, format, {
                request,
                columns: columns ?? visible,
                title,
                language,
                maxRows,
                offset: offset || undefined,
            });
            enqueueSnackbar(t("{{fileName}} downloaded", {fileName}), {variant: "success", autoHideDuration: 3000});
            return true;
        } catch (error) {
            console.error("Export failed", error);
            const message = error instanceof ServerTableExportError ? error.serverMessage : undefined;
            enqueueSnackbar(message ?? t("Could not complete this action. Please try again."), {variant: "error"});
            return false;
        } finally {
            closeSnackbar(progress);
            setRunning(undefined);
        }
    };

    return {
        disabled: Boolean(disabled) || readOnly || query.isLoading || query.isError || query.isPreviousData || nothingToExport,
        running,
        columns: allColumns(table, visible),
        visibleColumnIds: visible.map(column => column.id),
        pageRowCount: query.data?.content.length ?? 0,
        pageOffset: pageIndex * pageSize,
        totalRowCount,
        run,
    };
}
